'use client';

import React, { useEffect, useMemo, useState } from 'react';
import LandingFooter from '@/app/components/LandingFooter';
import PublicHero from '@/app/components/PublicHero';
import { createClient } from '@/lib/supabase/client';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useTranslation } from '@/i18n';

type FaqStatus = 'draft' | 'published';

type PlatformFaq = {
  id: string;
  question: string;
  answer: string;
  category: string;
  status: FaqStatus;
  is_featured: boolean;
  sort_order: number;
};

function normalizeText(value: unknown) {
  return String(value || '').trim();
}

function translateFaqQuestion(id: string, rawQuestion: string, t: any, locale: string): string {
  const dictKey = `publicWebsite:faqs.items.${id}.question`;
  const translated = t(dictKey);
  const base = translated || rawQuestion || '';
  const clean = base.trim();
  if (!clean) return clean;
  if (locale === 'ar') {
    // Strip trailing LTR ? marks; replace with Arabic ؟ once at end
    let s = clean.replace(/[?؟\s]+$/g, '');
    return `${s}؟`;
  }
  if (!/[?؟]$/.test(clean)) {
    return `${clean}?`;
  }
  return clean;
}

function translateFaqAnswer(id: string, rawAnswer: string, t: any): string {
  const dictKey = `publicWebsite:faqs.items.${id}.answer`;
  const translated = t(dictKey);
  return translated || rawAnswer || '';
}

export default function PublicFaqsPage() {
  const { t, locale } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [faqs, setFaqs] = useState<PlatformFaq[]>([]);

  const CATEGORY_TABS = useMemo(() => [
    { key: 'all', label: t('publicWebsite:faqs.categories.all') || (locale === 'ar' ? 'الكل' : 'All') },
    { key: 'general', label: t('publicWebsite:faqs.categories.general') || (locale === 'ar' ? 'عام' : 'General') },
    { key: 'subscription_billing', label: t('publicWebsite:faqs.categories.subscriptionBilling') || (locale === 'ar' ? 'الاشتراك والفوترة' : 'Subscription & Billing') },
    { key: 'documents_renewals', label: t('publicWebsite:faqs.categories.documentsRenewals') || (locale === 'ar' ? 'المستندات والتجديدات' : 'Documents & Renewals') },
    { key: 'client_portal', label: t('publicWebsite:faqs.categories.clientPortal') || (locale === 'ar' ? 'بوابة العملاء' : 'Client Portal') },
    { key: 'payments', label: t('publicWebsite:faqs.categories.payments') || (locale === 'ar' ? 'المدفوعات' : 'Payments') },
    { key: 'security', label: t('publicWebsite:faqs.categories.security') || (locale === 'ar' ? 'الأمان' : 'Security') },
    { key: 'support', label: t('publicWebsite:faqs.categories.support') || (locale === 'ar' ? 'الدعم' : 'Support') },
  ], [t, locale]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('platform_faqs')
          .select('id, question, answer, category, status, is_featured, sort_order')
          .eq('status', 'published')
          .order('sort_order', { ascending: true })
          .order('created_at', { ascending: true })
          .limit(500);
        if (error) throw error;
        if (cancelled) return;

        const rows = ((data as any) || []) as any[];
        const mapped: PlatformFaq[] = rows
          .map((r) => ({
            id: String(r.id),
            question: normalizeText(r.question),
            answer: normalizeText(r.answer),
            category: normalizeText(r.category) || 'general',
            status: (r.status as FaqStatus) || 'published',
            is_featured: r.is_featured === true,
            sort_order: Number(r.sort_order || 0),
          }))
          .filter((r) => r.id && r.question && r.answer);

        setFaqs(mapped);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || (t('publicWebsite:faqs.errorTitle') || (locale === 'ar' ? 'تعذّر تحميل الأسئلة الشائعة' : 'Could not load FAQs')));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [t, locale]);

  const filteredFaqs = useMemo(() => {
    if (activeCategory === 'all') return faqs;
    return faqs.filter((f) => f.category === activeCategory);
  }, [faqs, activeCategory]);

  return (
    <div className="min-h-screen bg-white">
      <PublicHero
        title={t('publicWebsite:faqs.heroTitle') || (locale === 'ar' ? 'الأسئلة الشائعة' : 'FAQs')}
        subtitle={t('publicWebsite:faqs.heroSubtitle') || (locale === 'ar' ? 'إجابات سريعة للأسئلة الشائعة حول بروفلو 360.' : 'Quick answers to common questions about ProFlow 360.')}
        crumbs={[{ label: t('publicWebsite:faqs.crumbsHome') || (locale === 'ar' ? 'الرئيسية' : 'Home'), href: '/' }, { label: t('publicWebsite:faqs.crumbsFaqs') || (locale === 'ar' ? 'الأسئلة الشائعة' : 'FAQs') }]}
      />

      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap items-center gap-2.5 mb-8">
            {CATEGORY_TABS.map((c) => {
              const selected = activeCategory === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => { setActiveCategory(c.key); setOpenId(null); }}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                    selected
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'bg-white text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                {t('publicWebsite:faqs.loading') || (locale === 'ar' ? 'جاري تحميل الأسئلة الشائعة...' : 'Loading FAQs...')}
              </div>
            </div>
          ) : error ? (
            <div className="card-base p-6 border border-danger/20">
              <p className="text-sm font-semibold text-foreground">{t('publicWebsite:faqs.errorTitle') || (locale === 'ar' ? 'تعذّر تحميل الأسئلة الشائعة' : 'Could not load FAQs')}</p>
              <p className="text-xs text-muted-foreground mt-1 break-words">{error}</p>
            </div>
          ) : filteredFaqs.length === 0 ? (
            <div className="card-base p-10 text-center">
              <p className="text-sm font-semibold text-foreground">{t('publicWebsite:faqs.empty.title') || (locale === 'ar' ? 'لا توجد أسئلة شائعة' : 'No FAQs found')}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('publicWebsite:faqs.empty.subtitle') || (locale === 'ar' ? 'جرّب فئة مختلفة، أو تحقق لاحقاً.' : 'Try a different category, or check back later.')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredFaqs.map((f) => {
                const isOpen = openId === f.id;
                const q = translateFaqQuestion(f.id, f.question, t, locale);
                const a = translateFaqAnswer(f.id, f.answer, t);
                return (
                  <div key={f.id} className="card-base overflow-hidden border border-border/80 hover:border-border transition-colors">
                    <button
                      type="button"
                      onClick={() => setOpenId((prev) => (prev === f.id ? null : f.id))}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                      aria-expanded={isOpen}
                    >
                      <span className={`text-sm sm:text-base ${isOpen ? 'font-bold text-primary' : 'font-semibold text-foreground'}`}>
                        {q}
                      </span>
                      <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-primary' : 'text-muted-foreground'}`} />
                    </button>

                    <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                      <div className="overflow-hidden">
                        <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                          {a}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
